import { useEffect, useMemo, useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Plus, Mic, ArrowLeft, Lightbulb, Workflow, ListChecks, Sparkles, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

import { ActionButton, SectionHeader } from '../components/VisualAtoms';
import SectionAnalyticsHeader from '../components/SectionAnalyticsHeader';
import FlowItemCard from '../components/FlowItemCard';
import LevitatingStageMap from '../components/LevitatingStageMap';
import { FLOW_STAGE_TONE, humanize } from '../theme/adminVisualSystem';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STAGES = [
  {
    key: 'ideas',
    label: 'Ideas',
    blurb: 'Capture raw exhibition ideas, production concepts, marketing seeds.',
    icon: Lightbulb,
  },
  {
    key: 'in_review',
    label: 'In Review',
    blurb: 'Score, weigh, and critically analyze before committing to execution.',
    icon: Workflow,
  },
  {
    key: 'todo',
    label: 'To Do List',
    blurb: 'Executable tasks with owners, due dates, and execution notes.',
    icon: ListChecks,
  },
];

function StageCircle__deprecated() { return null; }

function IdeasInputBar({ onCreate, onCreateWithAi }) {
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const [aiBusy, setAiBusy] = useState(false);

  const submit = async () => {
    const v = text.trim();
    if (!v) return;
    setBusy(true);
    try {
      await onCreate({ raw_input: v });
      setText('');
    } finally {
      setBusy(false);
    }
  };

  const submitAi = async () => {
    const v = text.trim();
    if (!v) return;
    setAiBusy(true);
    try {
      await onCreateWithAi(v);
      setText('');
    } finally {
      setAiBusy(false);
    }
  };

  return (
    <div
      className="border border-[#1a1a1a] bg-[#0d0d0d] p-4 space-y-3"
      data-testid="ideas-input-bar"
    >
      <SectionHeader
        title="Drop a raw idea"
        helper="Type freely — installation, marketing, ops, anything. Title and structure are AI-derivable."
      />
      <textarea
        value={text}
        onChange={(e) => setText(e.target.value)}
        rows={3}
        placeholder="Drop a raw idea for this exhibition…"
        data-testid="ideas-input"
        className="w-full bg-transparent border border-[#222] text-white font-mono text-sm p-3 focus:outline-none focus:border-[#90E0EF] placeholder:text-[#444] resize-none"
      />
      <div className="flex flex-wrap gap-2">
        <ActionButton
          variant="primary"
          icon={Plus}
          onClick={submit}
          disabled={busy || aiBusy || !text.trim()}
          data-testid="add-idea-btn"
        >
          {busy ? 'Adding…' : 'Add Idea'}
        </ActionButton>
        <ActionButton
          variant="purple"
          icon={aiBusy ? Loader2 : Sparkles}
          onClick={submitAi}
          disabled={busy || aiBusy || !text.trim()}
          data-testid="add-idea-ai-btn"
        >
          {aiBusy ? 'Structuring…' : 'Add + Structure with AI'}
        </ActionButton>
        <ActionButton
          variant="ghost"
          icon={Mic}
          onClick={() => toast.message('Voice idea capture will be added later.')}
          data-testid="voice-idea-btn"
        >
          Voice Idea
        </ActionButton>
      </div>
    </div>
  );
}

export default function ExhibitionFlowPage() {
  const { exhibitionId, selectedExhibition } = useOutletContext();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeStage, setActiveStage] = useState(null); // null = circles view; key = stage view
  const [search, setSearch] = useState('');
  const [sortMode, setSortMode] = useState('default');

  const fetchItems = () => {
    if (!exhibitionId) return;
    setLoading(true);
    axios
      .get(`${API}/admin/exhibition-flow?exhibition_id=${exhibitionId}`)
      .then((r) => setItems(r.data || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    fetchItems();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [exhibitionId]);

  const counts = useMemo(() => {
    const c = { ideas: 0, in_review: 0, todo: 0, done: 0, archived: 0 };
    for (const it of items) {
      if (it.status === 'done') c.done += 1;
      else if (it.status === 'archived') c.archived += 1;
      else if (c[it.stage] !== undefined) c[it.stage] += 1;
    }
    return c;
  }, [items]);

  const stats = useMemo(() => {
    const scored = items.filter((i) => i.idea_score != null);
    const avg = scored.length ? scored.reduce((a, b) => a + Number(b.idea_score), 0) / scored.length : null;
    const max = scored.length ? Math.max(...scored.map((i) => Number(i.idea_score))) : null;
    return { avg, max, scoredCount: scored.length };
  }, [items]);

  // ─── Mutations ─────────────────────────────────────────────
  const createItem = async (body) => {
    try {
      const res = await axios.post(`${API}/admin/exhibition-flow`, {
        exhibition_id: exhibitionId,
        ...body,
      });
      setItems((arr) => [res.data, ...arr]);
      toast.success('Idea added');
    } catch {
      toast.error('Could not save idea');
    }
  };

  const updateItem = async (id, body) => {
    try {
      const res = await axios.patch(`${API}/admin/exhibition-flow/${id}`, body);
      setItems((arr) => arr.map((it) => (it.id === id ? res.data : it)));
      toast.success('Saved');
    } catch {
      toast.error('Update failed');
    }
  };

  const moveItem = async (id, stage) => {
    try {
      const res = await axios.post(`${API}/admin/exhibition-flow/${id}/move`, { stage });
      setItems((arr) => arr.map((it) => (it.id === id ? res.data : it)));
      toast.success(`Moved to ${humanize(stage)}`);
    } catch {
      toast.error('Move failed');
    }
  };

  const archiveItem = async (id) => {
    try {
      await axios.post(`${API}/admin/exhibition-flow/${id}/archive`);
      setItems((arr) => arr.filter((it) => it.id !== id));
      toast.success('Archived');
    } catch {
      toast.error('Archive failed');
    }
  };

  const completeItem = async (id) => {
    try {
      await axios.post(`${API}/admin/exhibition-flow/${id}/complete`);
      setItems((arr) => arr.filter((it) => it.id !== id));
      toast.success('Marked done');
    } catch {
      toast.error('Could not complete');
    }
  };

  // ─── AI mutations ──────────────────────────────────────────
  const runAi = async (id, kind) => {
    const url =
      kind === 'structure'
        ? `${API}/admin/exhibition-flow/${id}/ai-structure`
        : `${API}/admin/exhibition-flow/${id}/ai-score`;
    setItems((arr) => arr.map((it) => (it.id === id ? { ...it, ai_status: 'ai_pending' } : it)));
    try {
      const res = await axios.post(url);
      const updated = res.data?.item;
      if (updated) {
        setItems((arr) => arr.map((it) => (it.id === id ? updated : it)));
      }
      toast.success(kind === 'structure' ? 'AI structured the idea' : 'AI scored the idea');
    } catch (err) {
      const detail = err?.response?.data?.detail || 'AI request failed.';
      try {
        const fresh = await axios.get(`${API}/admin/exhibition-flow?exhibition_id=${exhibitionId}`);
        setItems(fresh.data || []);
      } catch {
        /* ignore */
      }
      toast.error(`${kind === 'structure' ? 'AI structuring' : 'AI scoring'} failed. ${detail}`);
    }
  };

  const quickCreateWithAi = async (raw_idea) => {
    try {
      const res = await axios.post(`${API}/admin/exhibition-flow/quick-create-with-ai`, {
        exhibition_id: exhibitionId,
        raw_idea,
      });
      const created = res.data?.item;
      if (created) {
        setItems((arr) => [created, ...arr]);
      }
      toast.success('Idea created and structured by AI');
    } catch (err) {
      const detail = err?.response?.data?.detail || 'AI request failed.';
      toast.error(`Could not create + structure. ${detail}`);
    }
  };


  // Reset sort mode when switching stages so each stage's sensible default applies.
  useEffect(() => {
    setSortMode('default');
  }, [activeStage]);

  // Filtered + sorted view per stage
  const stageItems = useMemo(() => {
    let arr = items;
    if (activeStage) arr = arr.filter((i) => i.stage === activeStage);
    if (search) {
      const s = search.toLowerCase();
      arr = arr.filter(
        (i) =>
          (i.title || '').toLowerCase().includes(s) ||
          (i.raw_input || '').toLowerCase().includes(s) ||
          (i.description || '').toLowerCase().includes(s),
      );
    }

    // Stage-aware default sorting
    const PRI_RANK = { critical: 4, high: 3, medium: 2, low: 1 };
    const ts = (v) => (v ? new Date(v).getTime() : 0);
    const ds = (v) => (v ? new Date(v).getTime() : Number.MAX_SAFE_INTEGER); // missing due_date last
    const sorted = [...arr];
    const mode = sortMode === 'default' ? activeStage : sortMode;

    sorted.sort((a, b) => {
      switch (mode) {
        case 'in_review':
        case 'score':
          return (b.idea_score ?? -Infinity) - (a.idea_score ?? -Infinity)
            || ts(b.created_at) - ts(a.created_at);
        case 'todo':
        case 'priority':
          return (PRI_RANK[b.priority] || 0) - (PRI_RANK[a.priority] || 0)
            || ds(a.due_date) - ds(b.due_date)
            || ts(b.created_at) - ts(a.created_at);
        case 'newest':
        case 'ideas':
        default:
          return ts(b.created_at) - ts(a.created_at);
      }
    });
    return sorted;
  }, [items, activeStage, search, sortMode]);

  if (!exhibitionId) {
    return (
      <div className="border border-[#1a1a1a] bg-[#0d0d0d] p-12 text-center">
        <p className="text-[#666] font-mono text-sm">Select an exhibition from the topbar to view its flow.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="exhibition-flow-page">
      {/* Mini analytics header */}
      <SectionAnalyticsHeader
        title="Exhibition Flow"
        helper={`${selectedExhibition?.title || 'Exhibition'} — ideas, reviews, and execution tasks`}
        items={items}
        chartLabel="Idea Inflow"
        days={14}
        stats={[
          { label: 'Ideas', value: counts.ideas, tone: FLOW_STAGE_TONE.ideas },
          { label: 'In Review', value: counts.in_review, tone: FLOW_STAGE_TONE.in_review },
          { label: 'To Do', value: counts.todo, tone: FLOW_STAGE_TONE.todo },
          { label: 'Done', value: counts.done },
          { label: 'Avg Score', value: stats.avg != null ? stats.avg.toFixed(2) : '—' },
          { label: 'Top Score', value: stats.max != null ? stats.max.toFixed(2) : '—' },
        ]}
      />

      {/* Tabs (always visible secondary nav) */}
      <div className="flex flex-wrap items-center gap-2">
        <button
          onClick={() => setActiveStage(null)}
          data-clickable="true"
          data-testid="stage-tab-circles"
          className={`text-[10px] font-mono px-3 py-1.5 border rounded-none uppercase tracking-[0.14em] transition-all ${
            activeStage === null
              ? 'border-[#90E0EF] text-[#90E0EF] bg-[#90E0EF]/5 shadow-[0_0_10px_rgba(144,224,239,0.18)]'
              : 'border-[#222] text-[#777] hover:text-white hover:border-[#333]'
          }`}
        >
          Map
        </button>
        {STAGES.map((s) => (
          <button
            key={s.key}
            onClick={() => setActiveStage(s.key)}
            data-clickable="true"
            data-testid={`stage-tab-${s.key}`}
            className={`text-[10px] font-mono px-3 py-1.5 border rounded-none uppercase tracking-[0.14em] transition-all ${
              activeStage === s.key
                ? 'text-[#90E0EF] border-[#90E0EF] bg-[#90E0EF]/5'
                : 'border-[#222] text-[#777] hover:text-white hover:border-[#333]'
            }`}
            style={
              activeStage === s.key
                ? { color: FLOW_STAGE_TONE[s.key].text, borderColor: FLOW_STAGE_TONE[s.key].text }
                : undefined
            }
          >
            {s.label} · {counts[s.key]}
          </button>
        ))}
        {activeStage && (
          <div className="ml-auto flex items-center gap-2">
            <select
              value={sortMode}
              onChange={(e) => setSortMode(e.target.value)}
              data-testid="flow-sort"
              className="bg-[#0a0a0a] border border-[#222] text-white text-[10px] font-mono uppercase tracking-[0.14em] px-2 py-1.5 focus:outline-none focus:border-[#90E0EF]"
              title="Sort"
            >
              <option value="default">Sort · Default</option>
              <option value="newest">Newest</option>
              <option value="score">Highest Score</option>
              <option value="priority">Priority</option>
            </select>
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search…"
              data-testid="flow-search"
              className="bg-transparent border border-[#222] text-white text-xs font-mono px-3 py-1.5 w-48 focus:outline-none focus:border-[#90E0EF]"
            />
          </div>
        )}
      </div>

      {/* Body — levitating map OR stage zoom view */}
      <AnimatePresence mode="wait">
        {activeStage === null ? (
          <motion.section
            key="circles"
            initial={{ opacity: 0, scale: 1.04 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.92, filter: 'blur(6px)' }}
            transition={{ duration: 0.42, ease: [0.2, 0.8, 0.2, 1] }}
            className="relative"
          >
            <LevitatingStageMap
              stages={STAGES.map((s) => ({ key: s.key, label: s.label }))}
              counts={counts}
              onSelect={setActiveStage}
            />
          </motion.section>
        ) : (
          <motion.section
            key={activeStage}
            initial={{ opacity: 0, scale: 0.92, filter: 'blur(6px)' }}
            animate={{ opacity: 1, scale: 1, filter: 'blur(0px)' }}
            exit={{ opacity: 0, scale: 1.06, filter: 'blur(4px)' }}
            transition={{ duration: 0.42, ease: [0.2, 0.8, 0.2, 1] }}
            className="space-y-5"
            data-testid={`stage-view-${activeStage}`}
          >
            <div className="flex items-center gap-3">
              <button
                onClick={() => setActiveStage(null)}
                data-clickable="true"
                data-testid="back-to-stages"
                className="flex items-center gap-2 text-xs font-mono text-[#666] hover:text-white transition-colors"
              >
                <ArrowLeft className="w-4 h-4" /> Back to map
              </button>
              <span className="text-[10px] font-mono text-[#444]">·</span>
              <p
                className="text-[10px] font-mono tracking-[0.2em] uppercase"
                style={{ color: FLOW_STAGE_TONE[activeStage].text }}
              >
                {STAGES.find((s) => s.key === activeStage)?.label} · {counts[activeStage]}
              </p>
            </div>

            {/* Ideas input only on Ideas stage */}
            {activeStage === 'ideas' && (
              <IdeasInputBar onCreate={createItem} onCreateWithAi={quickCreateWithAi} />
            )}

            {/* Cards */}
            {loading ? (
              <p className="text-[#444] font-mono text-sm">Loading…</p>
            ) : stageItems.length === 0 ? (
              <div className="border border-[#1a1a1a] bg-[#0d0d0d] p-10 text-center">
                <p className="text-[#666] font-mono text-sm">No items in this stage yet.</p>
                {activeStage === 'ideas' && (
                  <p className="text-[11px] font-mono text-[#444] mt-2">
                    Drop a raw idea above to get started.
                  </p>
                )}
              </div>
            ) : (
              <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-3" data-testid={`stage-cards-${activeStage}`}>
                <AnimatePresence>
                  {stageItems.map((it) => (
                    <FlowItemCard
                      key={it.id}
                      item={it}
                      onUpdate={updateItem}
                      onMove={moveItem}
                      onArchive={archiveItem}
                      onComplete={completeItem}
                      onAi={runAi}
                    />
                  ))}
                </AnimatePresence>
              </div>
            )}
          </motion.section>
        )}
      </AnimatePresence>
    </div>
  );
}
